/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type {
  GanttCurrentTask,
  Gantt as GanttProps,
  GanttRow,
  GanttSelection as GanttSelectionProps,
  GanttTask,
  GanttTaskHover
} from '../Gantt.types';
import { Property } from 'csstype';
import { getTimePosition, parseDate } from '../../PRIVATE_TimeComponent/utils/timeUtils';
import { getResolvedRowVars, getResolvedTaskVars } from './ganttStyleUtils';
import { getSpatialIndex } from './spatialUtils';

type GanttRowLayoutObj<K1, K2> = {
  id: K1;
  y: number;
  height: number;
  depth?: number;
  expanded?: boolean;
  index: number;
  parentIndex?: number;
  isSelected: boolean;
  taskObjs: GanttTaskLayoutObj<K2>[];
};

type GanttTaskLayoutObj<K2> = {
  id: K2;
  renderIndex: number;
  index: number;
  rowIndex: number;
  y: number;
  height: number;
  borderRadius: string;
  fill: string;
  stroke: string;
  strokeWidth: number;
  isSelectable: boolean;
  isSelected: boolean;
  selectionEffect: {
    stroke: string;
    strokeWidth: number;
    padding: number;
  };
  hoverEffect: {
    stroke: string;
    strokeWidth: number;
    padding: number;
    shadow: {
      shadowColor: string;
      shadowOffsetX: number;
      shadowOffsetY: number;
      shadowBlur: number;
    };
  };
  focusEffect: {
    stroke: string;
    strokeWidth: number;
    padding: number;
  };
  startTime: number;
  endTime: number;
  overallStartTime: number;
  overallEndTime: number;
  rowLevel: number;
  previousAdjacentTaskObj?: GanttTaskLayoutObj<K2>;
  nextAdjacentTaskObj?: GanttTaskLayoutObj<K2>;
  labelObj?: {
    startTime: number;
    endTime: number;
    label: string;
    isInner: boolean;
    position: 'start' | 'center' | 'end';
  };
};

type GanttViewportRowLayoutObj<K1, K2> = Omit<GanttRowLayoutObj<K1, K2>, 'taskObjs'> & {
  taskObjs: GanttViewportTaskLayoutObj<K2>[];
};

type GanttViewportTaskLayoutObj<K2> = GanttTaskLayoutObj<K2> & {
  x: number;
  width: number;
};

type GanttViewPortTaskLabelLayoutObj = {
  x: number;
  y: number;
  width: number;
  height: number;
  label: string;
  isInner: boolean;
  textAlign: 'left' | 'center' | 'right';
  labelStyle?: {
    color?: Property.Color;
    fontFamily?: Property.FontFamily;
    fontSize?: Property.FontSize;
    fontStyle?: Property.FontStyle;
    fontWeight?: Property.FontWeight;
    textDecoration?: Property.TextDecoration;
  };
};

type ViewportLayout<K1, K2, D1 extends GanttRow<K1, D1, D2>, D2 extends GanttTask<K2>> = ReturnType<
  typeof getResolvedViewportLayout<K1, K2, D1, D2>
>;

/**
 * Determines whether given two intervals overlap
 * @param s1 interval 1 start
 * @param e1 interval 1 end
 * @param s2 interval 2 start
 * @param e2 interval 2 end
 * @param isClosedComparison True if closed interval comparison, else open
 * @returns Whether the two intervals overlap
 */
const isIntervalOverlap = (
  s1: number,
  e1: number,
  s2: number,
  e2: number,
  isClosedComparison = true
) => (isClosedComparison ? s1 <= e2 && s2 <= e1 : s1 < e2 && s2 < e1);

/**
 * Determines whether given two tasks overlap chronologically
 * @param taskObj1 task layout object
 * @param taskObj2 task layout object
 * @returns Whether the two tasks overlap chronologically
 */
const isOverlap = (
  taskObj1: { overallStartTime: number; overallEndTime: number },
  taskObj2: { overallStartTime: number; overallEndTime: number }
) => {
  // Open interval comparison: if interval 1 ends at the same time interval 2 starts, they are still NOT overlapping.
  return isIntervalOverlap(
    taskObj1.overallStartTime,
    taskObj1.overallEndTime,
    taskObj2.overallStartTime,
    taskObj2.overallEndTime,
    false
  );
};

/**
 * Binary search for the index of the leftmost element that equals the target.
 * If the target is not in the array, then the returned value is:
 *     - the index of rightmost element less than the target if approximateMode is 'predecessor'
 *     - the index of the leftmost element greater than the target if approximateMode is 'successor'
 * Search is O(lgN), N being the length of the array.
 * @param array The array
 * @param target The target
 * @param approximateMode The behavior if target is not in the array. 'predecessor' or 'successor'
 * @returns The leftmost index at which the target is found, or the index of the predecessor/successor element as per approximateMode param
 */
const binarySearchLeftMost = (
  array: number[],
  target: number,
  approximateMode: 'predecessor' | 'successor'
) => {
  if (array.length === 0) return 0;

  let L = 0;
  let R = array.length;
  while (L < R) {
    const m = Math.floor((L + R) / 2);
    const value = array[m];
    if (value < target) L = m + 1;
    else R = m;
  }
  // If target is not in the array, then L at this point is the rank of the target in the array,
  // i.e. the number of elements in the array that are less than the target.
  if (L < array.length) {
    const value = array[L];
    if (value === target || approximateMode === 'successor') return L;
  }
  // L - 1 would give the index of the rightmost element less than the target (predecessor)
  return L > 0 ? L - 1 : 0;
};

/**
 * Finds the minimum and maximum row indices relevant to the given viewport bounds
 * @param rowYs The row y coords
 * @param yMin The minimum y bound
 * @param yMax The maximum y bound
 * @returns The minimum and maximum row indices
 */
const findRowIndRange = (rowYs: number[], yMin: number, yMax: number) => {
  // O(lgN) binary search for first row (intersecting yMin)
  const minRowInd = binarySearchLeftMost(rowYs, yMin, 'predecessor');
  let maxRowInd = minRowInd;

  // linear search from that row to search for last row (intersecting yMax)
  for (let i = minRowInd; i < rowYs.length; i++) {
    if (rowYs[i] <= yMax) {
      maxRowInd = i;
    } else {
      break;
    }
  }
  return { minRowInd, maxRowInd };
};

/**
 * Gets the height of an empty row.
 * @param resolvedVars The resolved CSS vars.
 * @returns The height of an empty row.
 */
const getEmptyRowHeight = (resolvedVars: {
  rowPaddingTop: number;
  rowPaddingBottom: number;
  taskHeight: number;
}) => resolvedVars.rowPaddingTop + resolvedVars.taskHeight + resolvedVars.rowPaddingBottom;

/**
 * Computes the vertical layout of the given row and its tasks.
 * This method modifies the given task layout objects with resolved layout information.
 * @param param0
 * @returns The resolved height of the row.
 */
const calcRowTaskVerticalLayout = <K2>({
  rowHeight,
  rowY,
  taskObjs,
  resolvedVars
}: {
  rowHeight?: number;
  rowY: number;
  taskObjs: GanttTaskLayoutObj<K2>[];
  resolvedVars: ReturnType<typeof getResolvedRowVars> & ReturnType<typeof getResolvedTaskVars>;
}) => {
  if (taskObjs.length === 0) return getEmptyRowHeight(resolvedVars);

  // Handle properly in the future when we support these APIs
  const overlapOffset = undefined;

  taskObjs[0].rowLevel = 0;
  taskObjs[0].y = 0;
  const overlapChains = [[taskObjs[0]]];
  const rowLevelRecentTaskObjs = [taskObjs[0]];
  for (let i = 1; i < taskObjs.length; i++) {
    const taskObj = taskObjs[i];
    // Handle properly in the future when we support these APIs
    const overlapBehavior = 'stack';

    const previousTaskObj = taskObjs[i - 1];
    const thisOverlapsPrevious = isOverlap(taskObj, previousTaskObj);
    if (thisOverlapsPrevious) {
      overlapChains[overlapChains.length - 1].push(taskObj);
    } else {
      overlapChains.push([taskObj]);
    }

    let rowLevel = 0;
    let previousAdjacentTaskObj;
    switch (overlapBehavior) {
      case 'stack': {
        let promote = true;
        for (let j = 0; j < rowLevelRecentTaskObjs.length; j++) {
          previousAdjacentTaskObj = undefined;
          if (isOverlap(taskObj, rowLevelRecentTaskObjs[j])) {
            rowLevel += 1;
          } else {
            previousAdjacentTaskObj = rowLevelRecentTaskObjs[j];
            rowLevelRecentTaskObjs[j] = taskObj;
            promote = false;
            break;
          }
        }
        if (promote) {
          rowLevelRecentTaskObjs.push(taskObj);
        }
        taskObj.y = overlapOffset !== undefined ? rowLevel * overlapOffset : 0;
        if (previousAdjacentTaskObj) {
          taskObj.previousAdjacentTaskObj = previousAdjacentTaskObj;
          previousAdjacentTaskObj.nextAdjacentTaskObj = taskObj;
        }
        break;
      }
      // More cases in the future when we support the overlap behavior APIs
      default: {
        const _exhaustiveCheck: never = overlapBehavior;
        return _exhaustiveCheck;
      }
    }
    taskObj.rowLevel = rowLevel;
  }

  // Add padding to task y when row height is not fixed
  const rowPaddingTop = resolvedVars.rowPaddingTop;
  const rowPaddingBottom = resolvedVars.rowPaddingBottom;

  const rowLevelHeights = rowLevelRecentTaskObjs.map(() => 0);
  if (rowHeight === undefined) {
    // Figure out offset based on row level height
    if (overlapOffset === undefined) {
      taskObjs.forEach((taskObj) => {
        if (taskObj.height > rowLevelHeights[taskObj.rowLevel]) {
          rowLevelHeights[taskObj.rowLevel] = taskObj.height;
        }
      });
      const rowLevelCumHeightsWithPadding = rowLevelHeights
        .map((rowLevelHeight) => rowLevelHeight + rowPaddingTop + rowPaddingBottom)
        .reduce((acc, curr, i) => {
          acc.push((acc[i - 1] || 0) + curr);
          return acc;
        }, [] as number[]);
      taskObjs.forEach((taskObj) => {
        taskObj.y += (rowLevelCumHeightsWithPadding[taskObj.rowLevel - 1] || 0) + rowPaddingTop;
      });
    } else {
      taskObjs.forEach((taskObj) => {
        taskObj.y += rowPaddingTop;
      });
    }
  }

  const overlapChainHeights = overlapChains.map((chain) => {
    let chainHeight = 0;
    chain.forEach((chainTaskObj) => {
      const distanceFromBottomToRowTop = chainTaskObj.y + chainTaskObj.height;
      if (distanceFromBottomToRowTop > chainHeight) {
        chainHeight = distanceFromBottomToRowTop;
      }
    });
    return chainHeight;
  });

  let resolvedRowHeight: number;
  if (rowHeight === undefined) {
    // Grow the row height to accommodate everything
    resolvedRowHeight =
      overlapChainHeights.reduce((a, b) => {
        return Math.max(a, b);
      }) + rowPaddingTop;
    // If no overlapping offset specified: Mid align task within its row level
    if (overlapOffset === undefined) {
      overlapChains.forEach((chain) => {
        chain.forEach((taskObj) => {
          const rowLevelHeight = rowLevelHeights[taskObj.rowLevel];
          taskObj.y += (rowLevelHeight - taskObj.height) / 2 + rowY;
        });
      });
    } else {
      // Otherwise align all tasks to top (with offset)
      overlapChains.forEach((chain) => {
        chain.forEach((taskObj) => {
          taskObj.y += rowY;
        });
      });
    }
  } else {
    // Fix the row height and mid align everything
    resolvedRowHeight = rowHeight;
    overlapChains.forEach((chain, i) => {
      const chainHeight = overlapChainHeights[i];
      let minTaskY = Number.MAX_VALUE;
      chain.forEach((taskObj) => {
        taskObj.y += Math.floor((resolvedRowHeight - taskObj.height) / 2);
        if (taskObj.y < minTaskY) {
          minTaskY = taskObj.y;
        }
      });
      const offsetFromRowCenter = Math.floor((resolvedRowHeight - chainHeight) / 2) - minTaskY;
      chain.forEach((taskObj) => {
        taskObj.y += rowY + offsetFromRowCenter;
      });
    });
  }

  return resolvedRowHeight;
};

/**
 * Computes the (unresolved) task labels layout.
 * This method modifies the given task layout objects with resolved layout information.
 * @param taskObjs The task layout object.
 * @param rowData The rows data.
 */
const calcTaskLabelLayout = <K1, K2, D1 extends GanttRow<K1, D1, D2>, D2 extends GanttTask<K2>>(
  taskObjs: GanttTaskLayoutObj<K2>[],
  rowData: GanttProps<K2, D1, D2>['rows']
) => {
  // TODO: Support other label positions when we get to exposing those APIs
  // For now, just take care of label position "innerCenter"
  taskObjs.forEach((taskObj) => {
    const { index, rowIndex } = taskObj;
    const labelText = rowData[rowIndex].tasks[index].label;
    if (labelText) {
      taskObj.labelObj = {
        startTime: taskObj.startTime,
        endTime: taskObj.endTime,
        label: labelText,
        isInner: true,
        position: 'center' // Future: unless inner, 'start' if labelPosition is 'end', 'end if labelPosition is 'start'
        // TODO: Handle contrast color
      };
    }
  });
};

/**
 * Computes the resolved task label layout given the unresolved label layout and the viewport.
 * @param labelObj The unresolved label layout object.
 * @param labelStyle The label style.
 * @param y The y position of the task.
 * @param height The height of the task.
 * @param viewportStartTime The viewport start time.
 * @param viewportEndTime The viewport end time.
 * @param viewportWidth The viewport width.
 * @param isRTL Whether the reading direction is rtl.
 * @returns The resolved label layout object.
 */
const getResolvedTaskLabelLayout = <K2>(
  labelObj: GanttTaskLayoutObj<K2>['labelObj'],
  labelStyle: GanttViewPortTaskLabelLayoutObj['labelStyle'],
  y: number,
  height: number,
  viewportStartTime: number,
  viewportEndTime: number,
  viewportWidth: number,
  isRTL: boolean
) => {
  if (!labelObj) return;
  const startPos = getTimePosition(
    labelObj.startTime,
    viewportStartTime,
    viewportEndTime,
    viewportWidth,
    isRTL
  );
  const endPos = getTimePosition(
    labelObj.endTime,
    viewportStartTime,
    viewportEndTime,
    viewportWidth,
    isRTL
  );
  let textAlign: 'left' | 'center' | 'right';
  switch (labelObj.position) {
    case 'center':
      textAlign = 'center';
      break;
    case 'start':
      textAlign = isRTL ? 'right' : 'left';
      break;
    case 'end':
      textAlign = isRTL ? 'left' : 'right';
      break;
    default: {
      const _exhaustiveCheck: never = labelObj.position;
      return _exhaustiveCheck;
    }
  }
  return {
    x: isRTL ? endPos : startPos,
    y,
    width: isRTL ? startPos - endPos : endPos - startPos,
    height,
    label: labelObj.label,
    isInner: labelObj.isInner,
    textAlign,
    labelStyle
  };
};

/**
 * Gets normalized start and end date strings from props,
 * e.g. one of them may be undefined in the props, but that really means both
 * start and end are the same.
 * @param task The task data containing the start and end date strings.
 * @returns Normalized start and end date strings.
 */
const getNormalizedStartEnd = (task: { start?: string; end?: string }) => {
  const start = task.start ? task.start : task.end!;
  const end = task.end ? task.end : task.start!;
  return { start, end };
};

/**
 * Returns whether selection is supported given the selection mode.
 * @param selectionMode The selection mode from props.
 * @returns Whether selection is supported.
 */
const supportsSelection = (selectionMode?: 'none' | 'single' | 'multiple') => {
  return !!(selectionMode && selectionMode !== 'none');
};

/**
 * Gets the (unresolved) rows data layout. Note that this is done for all rows in the data.
 * @param rowData The rows data.
 * @param resolvedVars The resolved CSS vars.
 * @param gridlines The gridlines prop value.
 * @param selectionProps The selection related props.
 * @returns The (unresolved) rows data layout.
 */
const getRowDataLayout = <K1, K2, D1 extends GanttRow<K1, D1, D2>, D2 extends GanttTask<K2>>(
  rowData: GanttProps<K2, D1, D2>['rows'],
  resolvedVars: ReturnType<typeof getResolvedRowVars> & ReturnType<typeof getResolvedTaskVars>,
  gridlines: { horizontal: 'on' | 'off'; vertical: 'on' | 'off' },
  selectionProps: Omit<GanttSelectionProps<K2>, 'onSelectionChange'>
) => {
  const selectionEnabled = supportsSelection(selectionProps.selectionMode);
  const selectedIds = new Set(selectionProps.selectedIds);

  let rowY = 0;
  const rowObjs: GanttRowLayoutObj<K1, K2>[] = rowData.map((row, i) => {
    const taskObjs: GanttTaskLayoutObj<K2>[] = row.tasks.map((task, j) => {
      const { start, end } = getNormalizedStartEnd(task);
      const startTime = parseDate(start);
      const endTime = parseDate(end);
      return {
        id: task.id,
        renderIndex: 0, // calculated downstream
        index: j,
        rowIndex: i,
        y: -1, // calculated downstream
        startTime,
        endTime,
        overallStartTime: startTime,
        overallEndTime: endTime,
        rowLevel: 0, // calculated downstream
        height: task.height !== undefined ? task.height : resolvedVars.taskHeight,
        borderRadius:
          task.borderRadius !== undefined
            ? task.borderRadius.toString()
            : resolvedVars.taskBorderRadius,
        fill: task.color ? task.color : resolvedVars.taskBgColor,
        stroke: resolvedVars.taskBorderColor,
        strokeWidth: 1,
        isSelectable: selectionEnabled,
        isSelected: selectedIds.has(task.id),
        selectionEffect: {
          stroke: resolvedVars.taskEffectBorderColor,
          strokeWidth: 2,
          padding: 2
        },
        hoverEffect: {
          stroke: resolvedVars.taskEffectBorderColor,
          strokeWidth: 1,
          padding: 0,
          shadow: {
            ...resolvedVars.taskEffectBoxShadow
          }
        },
        focusEffect: {
          stroke: resolvedVars.taskEffectBorderColor,
          strokeWidth: 1,
          padding: 2
        }
      };
    });

    taskObjs.sort((a, b) => a.startTime - b.startTime);
    taskObjs.forEach((t, i) => (t.renderIndex = i));

    const resolvedRowHeight = calcRowTaskVerticalLayout({
      rowHeight: row.height,
      rowY,
      taskObjs,
      resolvedVars
    });
    calcTaskLabelLayout<K1, K2, D1, D2>(taskObjs, rowData);

    const rowObj = {
      id: row.id,
      y: rowY,
      height: resolvedRowHeight,
      index: i,
      isSelected: taskObjs.some((t) => t.isSelected),
      taskObjs
    };

    const horizontalLineHeightOffset = gridlines.horizontal === 'on' ? 1 : 0;

    rowY += resolvedRowHeight + horizontalLineHeightOffset;
    return rowObj;
  });

  const lastRowLayoutObj = rowObjs[rowObjs.length - 1];
  const totalRowsHeight = lastRowLayoutObj ? lastRowLayoutObj.y + lastRowLayoutObj.height : 0;

  const idTaskObjMap = new Map<K2, GanttTaskLayoutObj<K2>>();
  rowObjs.forEach((rowObj) =>
    rowObj.taskObjs.forEach((taskObj) => idTaskObjMap.set(taskObj.id, taskObj))
  );
  const idRowObjMap = new Map<K1, GanttRowLayoutObj<K1, K2>>();
  rowObjs.forEach((rowObj) => idRowObjMap.set(rowObj.id, rowObj));

  const spatialIndex = getSpatialIndex(rowObjs);

  return {
    rowData,
    rowObjs,
    rowPadding: resolvedVars.rowPaddingTop,
    totalRowsHeight,
    gridlines,
    spatialIndex,
    idRowObjMap,
    idTaskObjMap
  };
};

/**
 * Gets the corresponding task layout object given the id of the tasks, for each effect.
 * @param effectProps The task ids associated with each effect.
 * @param idTaskObjMap A Map between task ids and task layout objects.
 * @returns The task layout objects for each effect.
 */
const getEffectTaskObjs = <K2>(
  effectProps: {
    selectionProps: Omit<GanttSelectionProps<K2>, 'onSelectionChange'>;
    hoverProps: Omit<GanttTaskHover<K2>, 'onTaskHoverChange'>;
    currentProps: Omit<GanttCurrentTask<K2>, 'onCurrentTaskChange'>;
  },
  idTaskObjMap: Map<K2, GanttViewportTaskLayoutObj<K2>>
) => {
  // TODO: Decide whether the task selection source of truth should be in
  // the rowObjs layout, or determined dynamically here, or both.
  // Right now it's in both places.
  // Let's decide after we figure out where eactly to memo for performance.
  //
  // Note we're rendering selectedIds regardless of selectionMode.
  const selectedIds = effectProps.selectionProps.selectedIds || [];
  const selectedTaskObjs: GanttViewportTaskLayoutObj<K2>[] = [];
  selectedIds.forEach((id) => {
    const selectedTaskObj = idTaskObjMap.get(id);
    if (selectedTaskObj) selectedTaskObjs.push(selectedTaskObj);
  });

  const hoveredId = supportsSelection(effectProps.selectionProps.selectionMode)
    ? effectProps.hoverProps.hoveredTaskId
    : undefined;
  const hoveredTaskObj = hoveredId !== undefined ? idTaskObjMap.get(hoveredId) : undefined;

  const currentTask = effectProps.currentProps.currentTask;
  const isTaskFocused = currentTask?.isFocused === true;
  const currentTaskId = currentTask?.id;
  const focusedTaskObj =
    isTaskFocused && currentTaskId !== undefined ? idTaskObjMap.get(currentTaskId) : undefined;

  return {
    selectedTaskObjs,
    hoveredTaskObj,
    focusedTaskObj
  };
};

/**
 * Gets the resolved task layout object for the given viewport, given the unresolved task layout object.
 * @param taskObj The unresolved task layout object.
 * @param viewportStartTime The viewport start time.
 * @param viewportEndTime The viewport end time.
 * @param viewportWidth The viewport width.
 * @param scrollPosition The scroll position.
 * @param isRTL Whether the reading direction is rtl.
 * @returns The resolved task layout object.
 */
const getResolvedTaskObj = <K2>(
  taskObj: GanttTaskLayoutObj<K2>,
  viewportStartTime: number,
  viewportEndTime: number,
  viewportWidth: number,
  scrollPosition: number,
  isRTL: boolean
) => {
  const startPos = getTimePosition(
    taskObj.startTime,
    viewportStartTime,
    viewportEndTime,
    viewportWidth,
    isRTL
  );
  const endPos = getTimePosition(
    taskObj.endTime,
    viewportStartTime,
    viewportEndTime,
    viewportWidth,
    isRTL
  );
  return {
    ...taskObj,
    x: isRTL ? endPos : startPos,
    y: taskObj.y - scrollPosition,
    width: isRTL ? startPos - endPos : endPos - startPos
  };
};

/**
 * Gets the resolved rows data layout for the given viewport.
 * @param rowDataLayout The unresolved rows data layout object for all data.
 * @param viewportStartTime The viewport start time.
 * @param viewportEndTime The viewport end time.
 * @param scrollPosition The scroll position.
 * @param viewportWidth The viewport width.
 * @param viewportHeight The viewport height.
 * @param isRTL Whether the reading direction is rtl.
 * @returns The resolved rows data layout for the given viewport.
 */
const getResolvedViewportLayout = <
  K1,
  K2,
  D1 extends GanttRow<K1, D1, D2>,
  D2 extends GanttTask<K2>
>(
  rowDataLayout: ReturnType<typeof getRowDataLayout<K1, K2, D1, D2>>,
  viewportStartTime: number,
  viewportEndTime: number,
  scrollPosition: number,
  viewportWidth: number,
  viewportHeight: number,
  isRTL: boolean
) => {
  const viewportYMin = scrollPosition;
  const viewportYMax = scrollPosition + viewportHeight;
  const rowYs = rowDataLayout.rowObjs.map((r) => r.y);
  const { minRowInd, maxRowInd } = findRowIndRange(rowYs, viewportYMin, viewportYMax);

  const viewportRowsLayout: GanttViewportRowLayoutObj<K1, K2>[] = [];
  for (let i = minRowInd; i <= maxRowInd; i++) {
    const rowObj = rowDataLayout.rowObjs[i];
    const viewportTaskObjs = rowObj.taskObjs.filter(({ overallStartTime, overallEndTime }) =>
      isIntervalOverlap(overallStartTime, overallEndTime, viewportStartTime, viewportEndTime)
    );
    const resolvedViewportTaskObjs = viewportTaskObjs.map((taskObj) =>
      getResolvedTaskObj(
        taskObj,
        viewportStartTime,
        viewportEndTime,
        viewportWidth,
        scrollPosition,
        isRTL
      )
    );
    const resolvedViewportRowObjs = {
      ...rowObj,
      y: rowObj.y - scrollPosition,
      taskObjs: resolvedViewportTaskObjs
    };
    viewportRowsLayout.push(resolvedViewportRowObjs);
  }

  const viewportTaskLabelsLayout: GanttViewPortTaskLabelLayoutObj[] = [];
  viewportRowsLayout.forEach((rowObj) => {
    rowObj.taskObjs.forEach(({ index, rowIndex, labelObj, y, height }) => {
      const labelStyle = rowDataLayout.rowData[rowIndex].tasks[index].labelStyle;
      const labelLayout = getResolvedTaskLabelLayout(
        labelObj,
        labelStyle,
        y,
        height,
        viewportStartTime,
        viewportEndTime,
        viewportWidth,
        isRTL
      );
      if (labelLayout) viewportTaskLabelsLayout.push(labelLayout);
    });
  });

  // due to pixel hinting, odd value stroke width needs it's position to be offset by 0.5 to ensure consistent behavior across browsers
  // i.e. pos = pos + (strokeWidth % 2) * 0.5
  // e.g. stroke-width of 1px means 0.5px above and below the reference coordinate. With pixel hinting, some browsers
  // renders 1px above the reference, some renders 1px below the reference. If we offset the reference by 0.5px, the stroke location
  // becomes unambiguous (it'll lock onto whole pixel grid) so all browsers will render this consistently.
  const horizontalGridlinesPos =
    rowDataLayout.gridlines.horizontal === 'on'
      ? viewportRowsLayout.map(({ y, height }) => y + height + 0.5)
      : undefined;

  const idTaskObjMap = new Map<K2, GanttViewportTaskLayoutObj<K2>>();
  viewportRowsLayout.forEach((rowObj) =>
    rowObj.taskObjs.forEach((taskObj) => idTaskObjMap.set(taskObj.id, taskObj))
  );

  return {
    spatialIndex: rowDataLayout.spatialIndex,
    gridlines: rowDataLayout.gridlines,
    horizontalGridlinesPos,
    rowData: rowDataLayout.rowData,
    allRowObjs: rowDataLayout.rowObjs,
    allIdTaskObjMap: rowDataLayout.idTaskObjMap,
    allIdRowObjMap: rowDataLayout.idRowObjMap,
    rowPadding: rowDataLayout.rowPadding,
    rowObjs: viewportRowsLayout,
    taskLabelObjs: viewportTaskLabelsLayout,
    idTaskObjMap
  };
};

export {
  getRowDataLayout,
  getResolvedViewportLayout,
  getEffectTaskObjs,
  getNormalizedStartEnd,
  supportsSelection,
  getResolvedTaskObj
};
export type {
  GanttRowLayoutObj,
  GanttTaskLayoutObj,
  GanttViewportRowLayoutObj,
  GanttViewportTaskLayoutObj,
  ViewportLayout
};
